import { alpha, createTheme } from '@mui/material/styles';
import { getPaletteTokens } from './appSettings';

const FONT_STACK =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';

const CheckboxIcon = (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      height="16"
      rx="5"
      stroke="currentColor"
      strokeWidth="2"
      width="16"
      x="4"
      y="4.5"
    />
  </svg>
);

const CheckboxCheckedIcon = (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M9 3.5C5.68629 3.5 3 6.18629 3 9.5V15.5C3 18.8137 5.68629 21.5 9 21.5H15C18.3137 21.5 21 18.8137 21 15.5V9.5C21 6.18629 18.3137 3.5 15 3.5H9ZM16.7179 10.1961C17.1024 9.79966 17.0926 9.16657 16.6961 8.7821C16.2997 8.39763 15.6666 8.40737 15.2821 8.80385L10.6667 13.5635L8.7179 11.5539C8.33343 11.1574 7.70034 11.1476 7.30385 11.5321C6.90737 11.9166 6.89763 12.5497 7.2821 12.9461L9.94877 15.6961C10.1371 15.8904 10.3961 16 10.6667 16C10.9372 16 11.1962 15.8904 11.3846 15.6961L16.7179 10.1961Z"
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
);

const CheckboxIndeterminateIcon = (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M9 5.5H15C17.2091 5.5 19 7.29086 19 9.5V15.5C19 17.7091 17.2091 19.5 15 19.5H9C6.79086 19.5 5 17.7091 5 15.5V9.5C5 7.29086 6.79086 5.5 9 5.5ZM3 9.5C3 6.18629 5.68629 3.5 9 3.5H15C18.3137 3.5 21 6.18629 21 9.5V15.5C21 18.8137 18.3137 21.5 15 21.5H9C5.68629 21.5 3 18.8137 3 15.5V9.5ZM8 11.5C7.44772 11.5 7 11.9477 7 12.5C7 13.0523 7.44772 13.5 8 13.5H16C16.5523 13.5 17 13.0523 17 12.5C17 11.9477 16.5523 11.5 16 11.5H8Z"
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
);

function buildTheme(tokens, mode = 'light') {
  const p = tokens.primary;
  const pDark = tokens.primaryDark;
  const pLightest = tokens.primaryLightest;
  // Brand font/secondary/status ride on the tokens so a brand switch re-themes
  // wholesale; fallbacks preserve the Augment look if a token is absent.
  const font = tokens.font || FONT_STACK;

  return createTheme({
  palette: {
    mode,
    primary: {
      main: p,
      light: tokens.primaryLight,
      lighter: pLightest,
      dark: pDark,
      darker: tokens.primaryDarkest,
    },
    secondary: {
      main: tokens.secondary || '#06AED4',
      light: tokens.secondaryLight || '#7FD8F0',
      lighter: tokens.secondaryLightest || '#E0F7FF',
      dark: tokens.secondaryDark || '#0E7090',
    },
    success: {
      main: tokens.success || '#10B981',
      light: '#6EE7B7',
      lighter: '#ECFDF5',
      dark: tokens.successDark || '#0B815A',
    },
    warning: {
      main: tokens.warning || '#F79009',
      light: '#FDB022',
      lighter: '#FEFCE8',
      dark: tokens.warningDark || '#B54708',
    },
    error: {
      main: tokens.error || '#F04438',
      light: '#F97066',
      lighter: '#FEE4E2',
      dark: tokens.errorDark || '#B42318',
    },
    info: {
      main: tokens.info || '#06AED4',
      light: '#7FD8F0',
      lighter: '#E0F7FF',
      dark: tokens.infoDark || '#0E7090',
    },
    background: {
      default: tokens.bg,
      paper: tokens.paper,
    },
    text: {
      primary: tokens.text,
      secondary: tokens.textSec,
      disabled: '#9DA4AE',
    },
    divider: tokens.divider,
    grey: {
      50: '#F8F9FA',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D2D6DB',
      400: '#9DA4AE',
      500: '#6C737F',
      600: '#4D5761',
      700: '#2F3746',
      800: '#1C2536',
      900: '#111927',
    },
  },
  typography: {
    fontFamily: font,
    h1: { fontFamily: font, fontSize: '3.5rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontFamily: font, fontSize: '20px', fontWeight: 500, lineHeight: 1.2 },
    h3: { fontFamily: font, fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2 },
    h4: { fontFamily: font, fontSize: '2rem', fontWeight: 700, lineHeight: 1.2 },
    h5: { fontFamily: font, fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.2 },
    h6: { fontFamily: font, fontSize: '1rem', fontWeight: 500, lineHeight: 1.2 },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.57 },
    subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.57 },
    subtitle2: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.57 },
    button: { fontWeight: 600, textTransform: 'none', fontSize: '1rem', lineHeight: 1.5 },
    caption: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.66 },
    overline: {
      fontSize: '0.875rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      lineHeight: 2.5,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: 14,
          minHeight: 30,
          minWidth: 30,
          fontWeight: 500,
          letterSpacing: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
        },
        sizeSmall: { padding: '6px 16px' },
        sizeMedium: { padding: '8px 20px' },
        sizeLarge: { padding: '11px 24px' },
        textSizeSmall: { padding: '7px 12px' },
        textSizeMedium: { padding: '9px 16px' },
        textSizeLarge: { padding: '12px 16px' },
        containedPrimary: {
          background: p,
          '&:hover': {
            background: pDark,
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0px 5px 22px rgba(0, 0, 0, 0.04), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.03)',
          border: 'none',
          backgroundImage: 'none',
          '&.right-bordered': {
            borderRadius: 0,
            borderRight: '1px solid',
            '&.no-border': { borderRight: 'none' },
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '32px 24px',
          '&:last-child': { paddingBottom: '32px' },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: 'h6' },
        subheaderTypographyProps: { variant: 'body2' },
      },
      styleOverrides: {
        root: { padding: '32px 24px 16px' },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'primary',
        icon: CheckboxIcon,
        checkedIcon: CheckboxCheckedIcon,
        indeterminateIcon: CheckboxIndeterminateIcon,
      },
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: p },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: 14,
        },
        colorPrimary: {
          backgroundColor: pLightest,
          color: pDark,
        },
        outlinedPrimary: {
          borderColor: p,
          color: p,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '24px',
          '&::placeholder': { opacity: 1 },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& fieldset': {
            borderColor: '#E5E7EB',
            borderWidth: 1.5,
          },
          '&:hover fieldset': {
            borderColor: '#D2D6DB',
            borderWidth: 1.5,
          },
          '&.Mui-focused fieldset': {
            borderColor: p,
            borderWidth: 2,
          },
        },
        input: {
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '24px',
          padding: '14px 14px',
        },
        sizeSmall: {
          input: {
            padding: '9px 12px',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: 8,
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: '#E5E7EB',
          overflow: 'hidden',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:before': { display: 'none' },
          '&:after': { display: 'none' },
          '&:hover': {
            backgroundColor: mode === 'dark' ? alpha('#fff', 0.04) : 'rgba(17, 25, 39, 0.04)',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
            borderColor: p,
            boxShadow: `0 0 0 2px ${p}`,
          },
        },
        input: {
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '24px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 16,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          '& .MuiFormLabel-asterisk': { color: '#F04438' },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '&.editable-tasks-drawer': {
            '& .MuiTableCell-body': {
              paddingBottom: '0 !important',
              paddingTop: '0 !important',
              '&:last-child': {
                paddingRight: '0 !important',
              },
            },
            '&.standardPadding': {
              '& .MuiTableCell-body': {
                paddingBottom: '16px !important',
              },
            },
          },
          '&.noRowBorders': {
            '& .MuiTableCell-root': {
              borderBottom: 'none',
            },
          },
          '& .required-asterisk': {
            color: '#F04438',
            verticalAlign: 'top',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { border: '1px solid #E5E7EB' },
      },
    },
    MuiPopover: {
      defaultProps: { elevation: 16 },
    },
    MuiRadio: {
      defaultProps: { color: 'primary' },
    },
    MuiSwitch: {
      defaultProps: { color: 'primary' },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          color: t.palette.text.secondary,
          '&:hover': {
            backgroundColor: alpha(p, 0.08),
            color: p,
          },
        }),
        sizeSmall: { padding: 4 },
        colorPrimary: {
          color: p,
          '&:hover': { backgroundColor: alpha(p, 0.12) },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#111927',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          borderBottom: '1px solid #E5E7EB',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          backgroundColor: t.palette.background.paper,
          color: t.palette.text.primary,
          backgroundImage: 'none',
          borderRight: `1px solid ${t.palette.divider}`,
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          '&.Mui-selected': {
            backgroundColor: pLightest,
            color: p,
            '&:hover': { backgroundColor: tokens.primaryLight },
          },
          '&:hover': { backgroundColor: '#F8F9FA' },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 20 },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(17, 25, 39, 0.4)',
          '&.lighter': {
            backgroundColor: 'rgba(17, 25, 39, 0.35) !important',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: '#111927',
          padding: '12px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 3px 14px rgba(0, 0, 0, 0.08)',
          fontSize: 12,
          '& .MuiTooltip-arrow': {
            color: '#FFFFFF',
            '&:before': {
              boxShadow: '0 0 5px 0px rgba(0, 0, 0, 0.2)',
            },
          },
        },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          border: 'none',
          fontSize: '0.875rem',
          '& .MuiDataGrid-columnHeaders': {
            borderRadius: 0,
            overflow: 'hidden',
          },
          '& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeadersInner--scrollable': {
            overflow: 'hidden',
          },
          '& .MuiDataGrid-scrollbar.MuiDataGrid-scrollbar--horizontal': {
            display: 'none',
          },
          '& .MuiDataGrid-toolbarContainer': {
            '& .MuiInputBase-root': {
              borderRadius: '12px',
            },
            '& .MuiButton-root': {
              borderRadius: '12px',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '1rem',
            },
            '& .toolbar-column-button, & .toolbar-filter-button, & .toolbar-density-button, & .toolbar-export-button, & .toolbar-icon-buttons': {
              '&:hover': {
                backgroundColor: t.palette.mode === 'dark'
                  ? alpha(t.palette.common.white, 0.04)
                  : alpha(t.palette.common.black, 0.04),
              },
            },
          },
          '& .MuiDataGrid-columnsManagement': {
            '& .MuiFormControlLabel-root .MuiTypography-root': {
              fontSize: '16px',
            },
          },
          '& .MuiCheckbox-root': {
            color: t.palette.grey[400],
            '&.Mui-checked': {
              color: t.palette.primary.main,
            },
          },
        }),
        columnHeaders: {
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.5px',
        },
        cell: {
          borderTop: 'none',
        },
        row: ({ theme: t }) => ({
          backgroundColor: t.palette.background.paper,
        }),
        footerContainer: {
          backgroundColor: 'background.paper',
        },
        toolbarContainer: {
          backgroundColor: 'background.paper',
        },
      },
    },
  },
});
}

export function createAugmentTheme(settings) {
  const tokens = getPaletteTokens(settings);
  const mode = settings.paletteMode === 'dark' ? 'dark' : 'light';
  return buildTheme(tokens, mode);
}

const augmentTheme = createAugmentTheme({
  colorPreset: 'dogwoodRose',
  paletteMode: 'light',
  navColor: 'blend-in',
  contrast: 'high',
});

export default augmentTheme;
